// src/pages/redress/LiabilityScreen.tsx
//
// Six-gate liability decision screen for the FCA Motor Finance Redress Scheme
// (Phase 2 — M6-2). Implements the full gate chain from the Phase 2 workflow:
//
//   Gate 1 — Scheme case?            (BoolRadio → reason select if No)
//   Gate 2 — Subject matter?         (BoolRadio → exclusion select if No)
//   Gate 3 — Relevant arrangement?   (DCA / High commission / Tied checkboxes + commission £)
//   Gate 4 — Exceptions?             (de minimis / zero APR / DCA-not-acted / captive checkboxes)
//   Gate 5 — Unfair relationship?    (BoolRadio → fairness reason if No)
//   Gate 6 — Loss or damage?         (BoolRadio → narrative + evidence option if No)
//
// Each "No" gate renders a dangerLight closure panel with a "Save and close"
// button, except Gate 1 / high_value_threshold which renders a warn banner
// and a "Mark held" button instead.
//
// Props include agreement + agreementId (per M6-1 wiring in RedressTab).

import React, { useMemo, useState } from 'react';

import {
  Agreement,
  CloseLiabilityResponse,
  FairnessReason,
  GateExceptions,
  GateLossOrDamage,
  GateRelevantArrangement,
  GateSchemeCase,
  GateSubjectMatter,
  GateUnfairRelationship,
  Liability,
  LiabilityClosure,
  NotSchemeCaseReason,
  RedressCase,
  RedressStatus,
  SubjectMatterExclusion,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  bannerInfoStyle,
  bannerSuccessStyle,
  bannerWarnStyle,
  buttonDanger,
  buttonPrimary,
  buttonSecondary,
  colors,
  fieldsetStyle,
  humaniseStatus,
  inputStyle,
  labelStackStyle,
  legendStyle,
} from './styles';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LiabilityScreenProps {
  claimId: string;
  redressCase: RedressCase;
  agreement: Agreement;
  agreementId: string;
  onCaseUpdated: (c: RedressCase) => void;
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const CLOSED_STATUSES: readonly RedressStatus[] = [
  'closed_no_commission',
  'closed_no_arrangement',
  'closed_no_unfair_relationship',
  'closed_no_loss',
  'closed_not_scheme_case',
  'closed_outside_subject_matter',
  'closed_exception_exists',
];

function isClosedStatus(s: RedressStatus): boolean {
  return (CLOSED_STATUSES as readonly string[]).includes(s);
}

const HIGH_VALUE_STATUS: RedressStatus = 'held_high_value';

// ---------------------------------------------------------------------------
// Option constants (verbatim §5 lists)
// ---------------------------------------------------------------------------

const NOT_SCHEME_REASONS: Array<{ value: NotSchemeCaseReason; label: string }> = [
  { value: 'out_of_scheme_date_range', label: 'Out of scheme date range' },
  { value: 'not_regulated', label: 'Not regulated' },
  { value: 'no_commission_arrangements', label: 'No commission arrangements' },
  { value: 'no_commission_payable', label: 'No commission payable' },
  { value: 'high_value_threshold', label: 'High-value threshold (held — not a closure)' },
  { value: 'cooling_off_cancellation', label: 'Cooling-off cancellation' },
  { value: 'limitation_expired', label: 'Limitation expired' },
  { value: 'prior_redress_accepted', label: 'Prior redress accepted' },
  { value: 'full_and_final_settlement', label: 'Full and final settlement' },
  { value: 'fos_already_determined', label: 'FOS already determined' },
  { value: 'court_or_tribunal_decided', label: 'Court/tribunal decided (opt-out)' },
];

const SUBJECT_MATTER_EXCLUSIONS: Array<{ value: SubjectMatterExclusion; label: string }> = [
  { value: 'adequate_disclosure_dca', label: 'Adequate disclosure — DCA' },
  { value: 'adequate_disclosure_commission', label: 'Adequate disclosure — commission' },
  { value: 'adequate_disclosure_tied', label: 'Adequate disclosure — tied' },
  { value: 'adequate_disclosure_other_broker_incentive', label: 'Adequate disclosure — other broker incentive' },
];

const FAIRNESS_REASONS: Array<{ value: FairnessReason; label: string }> = [
  { value: 'adequate_disclosure', label: 'Adequate disclosure' },
  { value: 'consumer_sophisticated', label: 'Consumer sophisticated' },
  { value: 'tied_arrangement_not_operated_in_practice', label: 'Tied arrangement not operated in practice (FCA notification req.)' },
];

// ---------------------------------------------------------------------------
// cloneForEdit — build the six-gate Liability from agreement.liability
// ---------------------------------------------------------------------------

function cloneForEdit(liability: Liability | undefined): Liability {
  const src: Liability = liability ?? {};
  return {
    schemeCase: { ...(src.schemeCase ?? {}) } as GateSchemeCase,
    subjectMatter: { ...(src.subjectMatter ?? {}) } as GateSubjectMatter,
    relevantArrangement: { ...(src.relevantArrangement ?? {}) } as GateRelevantArrangement,
    exceptions: { ...(src.exceptions ?? {}) } as GateExceptions,
    unfairRelationship: { ...(src.unfairRelationship ?? {}) } as GateUnfairRelationship,
    lossOrDamage: { ...(src.lossOrDamage ?? {}) } as GateLossOrDamage,
    closure: src.closure ? { ...src.closure } : undefined,
    rebuttalNotes: src.rebuttalNotes ?? '',
  };
}

// ---------------------------------------------------------------------------
// Arrangement / exception helpers
// ---------------------------------------------------------------------------

function anyArrangement(ra: GateRelevantArrangement | undefined): boolean {
  if (!ra) return false;
  return Boolean(ra.dca) || Boolean(ra.highCommission) || Boolean(ra.tied);
}

function anyException(ex: GateExceptions | undefined): boolean {
  if (!ex) return false;
  return (
    Boolean(ex.deMinimis) ||
    Boolean(ex.zeroApr) ||
    Boolean(ex.dcaPresentNotActedUpon) ||
    Boolean(ex.captiveOrWhiteLabelTied)
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LiabilityScreen({
  claimId,
  redressCase,
  agreement,
  agreementId,
  onCaseUpdated,
}: LiabilityScreenProps): React.ReactElement {
  const [draft, setDraft] = useState<Liability>(() =>
    cloneForEdit(agreement.liability),
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [closing, setClosing] = useState<boolean>(false);
  const [transitioning, setTransitioning] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  // Informational (non-failure) outcome — e.g. "answers saved, nothing
  // to close" from the close action (F3 polish).
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadingLetter, setDownloadingLetter] = useState<boolean>(false);

  const agreementStatus = agreement.workflow.status;
  const caseClosed = isClosedStatus(agreementStatus);
  const isHighValue = agreementStatus === HIGH_VALUE_STATUS;
  const locked = caseClosed;

  const hasArrangement = useMemo(
    () => anyArrangement(draft.relevantArrangement),
    [draft.relevantArrangement],
  );

  const hasException = useMemo(
    () => anyException(draft.exceptions),
    [draft.exceptions],
  );

  // Closure info from the persisted liability (not draft)
  const liabilityClosure: LiabilityClosure | undefined = agreement.liability?.closure;

  // ---------------------------------------------------------------------------
  // Gate visibility chain
  // Gate 1: always shown
  // Gate 2: gate 1 is Yes
  // Gate 3: gate 1 Yes + gate 2 Yes
  // Gate 4: gate 1 Yes + gate 2 Yes (shown alongside gate 3 area)
  // Gate 5: gate 3 has arrangement AND gate 4 has no exception
  // Gate 6: gate 5 is Yes
  // ---------------------------------------------------------------------------

  const showGate2 = draft.schemeCase?.isSchemeCase === true;
  const showGate3 = showGate2 && draft.subjectMatter?.inSubjectMatter === true;
  const showGate4 = showGate3;
  const showGate5 = showGate3 && hasArrangement && !hasException;
  const showGate6 = showGate5 && draft.unfairRelationship?.present === true;

  // ---------------------------------------------------------------------------
  // Field updaters
  // ---------------------------------------------------------------------------

  function updateSchemeCase(patch: Partial<GateSchemeCase>): void {
    setDraft((prev) => ({
      ...prev,
      schemeCase: { ...(prev.schemeCase ?? {}), ...patch },
    }));
  }

  function updateSubjectMatter(patch: Partial<GateSubjectMatter>): void {
    setDraft((prev) => ({
      ...prev,
      subjectMatter: { ...(prev.subjectMatter ?? {}), ...patch },
    }));
  }

  function updateArrangement(patch: Partial<GateRelevantArrangement>): void {
    setDraft((prev) => ({
      ...prev,
      relevantArrangement: { ...(prev.relevantArrangement ?? {}), ...patch },
    }));
  }

  function updateExceptions(patch: Partial<GateExceptions>): void {
    setDraft((prev) => ({
      ...prev,
      exceptions: { ...(prev.exceptions ?? {}), ...patch },
    }));
  }

  function updateUnfairRelationship(patch: Partial<GateUnfairRelationship>): void {
    setDraft((prev) => ({
      ...prev,
      unfairRelationship: { ...(prev.unfairRelationship ?? {}), ...patch },
    }));
  }

  function updateLossOrDamage(patch: Partial<GateLossOrDamage>): void {
    setDraft((prev) => ({
      ...prev,
      lossOrDamage: { ...(prev.lossOrDamage ?? {}), ...patch },
    }));
  }

  // ---------------------------------------------------------------------------
  // Network actions
  // ---------------------------------------------------------------------------

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setMessage(null);
    setInfo(null);
    try {
      const updated = await redressApi.saveLiability(
        claimId,
        redressCase.id,
        agreementId,
        draft,
      );
      onCaseUpdated(updated);
      const updatedAgreement = updated.agreements.find(
        (a) => a.agreementId === agreementId,
      );
      setDraft(cloneForEdit(updatedAgreement?.liability));
      setMessage('Liability saved.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to save liability'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (): Promise<void> => {
    setClosing(true);
    setError(null);
    setMessage(null);
    setInfo(null);
    try {
      const res: CloseLiabilityResponse = await redressApi.closeLiability(
        claimId,
        redressCase.id,
        agreementId,
        draft,
      );
      onCaseUpdated(res.case);
      const updatedAgreement = res.case.agreements.find(
        (a) => a.agreementId === agreementId,
      );
      setDraft(cloneForEdit(updatedAgreement?.liability));
      // M13a: the backend returns {case, closure, highValueHold} — the
      // old closureStatus/closureReason keys never existed, so this
      // message rendered "Agreement closed (undefined)".
      // (F3 polish: a 200 always carries a closure unless it's the
      // high-value hold — the no-closure outcome is the structured 400
      // handled in the catch below, so no third branch exists here.)
      if (res.highValueHold) {
        setMessage(
          'Agreement held — high value (pending FCA guidance).',
        );
      } else if (res.closure !== null) {
        setMessage(
          `Agreement closed (${humaniseStatus(res.closure.status)})${
            res.closure.letterType
              ? ` — ${res.closure.letterType} closure letter applies`
              : ''
          }.`,
        );
      }
    } catch (err: unknown) {
      // F3 polish: the backend 400s with code 'no_closure_required' when
      // the gate review is incomplete OR every gate passes — either way
      // the liability answers WERE saved (step 2 of the close route), so
      // this is information, not a failure.
      const code = (err as {
        response?: { status?: number; data?: { code?: string } };
      })?.response?.data?.code;
      if (code === 'no_closure_required') {
        setInfo(
          'Liability answers saved — no closure condition met; ' +
            'proceed to Calculations.',
        );
      } else {
        setError(extractError(err, 'Failed to close case'));
      }
    } finally {
      setClosing(false);
    }
  };

  const handleMarkHighValue = async (): Promise<void> => {
    setTransitioning(true);
    setError(null);
    setMessage(null);
    setInfo(null);
    try {
      const updated = await redressApi.transition(
        claimId,
        redressCase.id,
        agreementId,
        'held_high_value',
      );
      onCaseUpdated(updated);
      setMessage('Agreement marked as held — high value.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to mark high-value hold'));
    } finally {
      setTransitioning(false);
    }
  };

  const handleDownloadLetter = async (): Promise<void> => {
    setDownloadingLetter(true);
    setError(null);
    try {
      const blob = await redressApi.downloadClosureLetter(
        claimId,
        redressCase.id,
        agreementId,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redress-closure-${redressCase.proclaimReference}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to download closure letter'));
    } finally {
      setDownloadingLetter(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Closed banner */}
      {caseClosed ? (
        <ClosedBanner
          status={agreementStatus}
          onDownload={() => void handleDownloadLetter()}
          downloading={downloadingLetter}
        />
      ) : null}

      {/* High-value held banner (inputs stay unlocked) */}
      {isHighValue ? (
        <div style={bannerWarnStyle} role="status">
          <span>
            <strong>Held — high value.</strong> Awaiting manual progression;
            the high-value client email has been queued.
          </span>
        </div>
      ) : null}

      {/* Closure summary (persisted closure block) */}
      {caseClosed && liabilityClosure ? (
        <div style={bannerInfoStyle} role="status">
          <strong>Closure:</strong> letter type{' '}
          <code>{liabilityClosure.letterType ?? '—'}</code>;{' '}
          {liabilityClosure.objectable
            ? 'client may object'
            : 'not objectable (FOS only)'}
          ; FOS window {liabilityClosure.fosWindowMonths ?? 6} months.
        </div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Gate 1 — Scheme case?                                               */}
      {/* ------------------------------------------------------------------ */}
      <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 1 — Scheme case">
        <legend style={legendStyle}>Gate 1 — Scheme case</legend>

        <BoolRadio
          label="Is this a scheme case?"
          name="isSchemeCase"
          value={draft.schemeCase?.isSchemeCase}
          onChange={(v) => updateSchemeCase({ isSchemeCase: v })}
        />

        {draft.schemeCase?.isSchemeCase === false ? (
          <div style={closureBoxStyle}>
            <label style={labelStackStyle}>
              Reason not a scheme case
              <select
                style={inputStyle}
                value={draft.schemeCase?.notSchemeCaseReason ?? ''}
                onChange={(e) =>
                  updateSchemeCase({
                    notSchemeCaseReason: (e.target.value as NotSchemeCaseReason) || null,
                  })
                }
              >
                <option value="">— select reason —</option>
                {NOT_SCHEME_REASONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            {draft.schemeCase?.notSchemeCaseReason === 'high_value_threshold' ? (
              /* High-value path: warn + "Mark held" — NOT a closure */
              <div style={{ ...bannerWarnStyle, marginTop: 12, marginBottom: 0 }} role="note">
                <span>
                  <strong>High-value threshold</strong> — agreement is held open;
                  send the high-value client email. This is not a closure.
                </span>
                <button
                  type="button"
                  disabled={transitioning || locked}
                  onClick={() => void handleMarkHighValue()}
                  style={applyDisabled(buttonSecondary, transitioning || locked)}
                  aria-busy={transitioning}
                >
                  {transitioning ? 'Saving…' : 'Mark held (high value)'}
                </button>
              </div>
            ) : (
              /* All other reasons: standard closure button */
              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={{ ...applyDisabled(buttonDanger, closing || locked), marginTop: 8 }}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (not a scheme case)'}
              </button>
            )}
          </div>
        ) : null}
      </fieldset>

      {/* ------------------------------------------------------------------ */}
      {/* Gate 2 — Subject matter?                                            */}
      {/* ------------------------------------------------------------------ */}
      {showGate2 ? (
        <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 2 — Subject matter">
          <legend style={legendStyle}>Gate 2 — Subject matter</legend>

          <BoolRadio
            label="Is the complaint within subject matter?"
            name="inSubjectMatter"
            value={draft.subjectMatter?.inSubjectMatter}
            onChange={(v) => updateSubjectMatter({ inSubjectMatter: v })}
          />

          {draft.subjectMatter?.inSubjectMatter === false ? (
            <div style={closureBoxStyle}>
              <label style={labelStackStyle}>
                Exclusion reason
                <select
                  style={inputStyle}
                  value={draft.subjectMatter?.exclusion ?? ''}
                  onChange={(e) =>
                    updateSubjectMatter({
                      exclusion: (e.target.value as SubjectMatterExclusion) || null,
                    })
                  }
                >
                  <option value="">— select exclusion —</option>
                  {SUBJECT_MATTER_EXCLUSIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={{ ...applyDisabled(buttonDanger, closing || locked), marginTop: 8 }}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (outside subject matter)'}
              </button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Gate 3 — Relevant arrangement?                                      */}
      {/* ------------------------------------------------------------------ */}
      {showGate3 ? (
        <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 3 — Relevant arrangement">
          <legend style={legendStyle}>Gate 3 — Relevant arrangement</legend>

          <div style={{ marginBottom: 8, fontSize: 13, color: colors.neutral600 }}>
            Tick each arrangement type that applies. High commission = ≥39% of
            total cost of credit and ≥10% of loan.
          </div>

          <div style={arrangementRowStyle}>
            <ArrangementCheckbox
              label="DCA"
              checked={Boolean(draft.relevantArrangement?.dca)}
              onChange={(v) => updateArrangement({ dca: v })}
            />
            <ArrangementCheckbox
              label="High Commission"
              checked={Boolean(draft.relevantArrangement?.highCommission)}
              onChange={(v) => updateArrangement({ highCommission: v })}
            />
            <ArrangementCheckbox
              label="Tied"
              checked={Boolean(draft.relevantArrangement?.tied)}
              onChange={(v) => updateArrangement({ tied: v })}
            />
          </div>

          <label style={{ ...labelStackStyle, maxWidth: 260, marginTop: 16 }}>
            Total commission paid (£)
            <input
              type="number"
              step="0.01"
              min="0"
              value={
                draft.relevantArrangement?.totalCommissionPaid === undefined ||
                draft.relevantArrangement?.totalCommissionPaid === null
                  ? ''
                  : String(draft.relevantArrangement.totalCommissionPaid)
              }
              onChange={(e) => {
                const raw = e.target.value;
                updateArrangement({
                  totalCommissionPaid: raw === '' ? null : Number(raw),
                });
              }}
              style={inputStyle}
            />
          </label>

          {/* No-arrangement closure (arrangement section shown but nothing ticked) */}
          {!hasArrangement ? (
            <div style={{ ...closureBoxStyle, marginTop: 16 }}>
              <div style={{ fontSize: 13, color: colors.neutral700, marginBottom: 8 }}>
                No relevant arrangement is ticked yet. If none apply, close the
                agreement below.
              </div>
              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={applyDisabled(buttonDanger, closing || locked)}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (no arrangement)'}
              </button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Gate 4 — Exceptions?                                                */}
      {/* ------------------------------------------------------------------ */}
      {showGate4 ? (
        <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 4 — Exceptions">
          <legend style={legendStyle}>Gate 4 — Exceptions</legend>

          <div style={{ marginBottom: 8, fontSize: 13, color: colors.neutral600 }}>
            Tick any exception that applies. If any is ticked the case will
            close.
          </div>

          <div style={reasonsGridStyle}>
            <CheckboxRow
              label="De minimis (£120 Scheme 1 / £150 Scheme 2)"
              checked={Boolean(draft.exceptions?.deMinimis)}
              onChange={(v) => updateExceptions({ deMinimis: v })}
            />
            <CheckboxRow
              label="Zero APR"
              checked={Boolean(draft.exceptions?.zeroApr)}
              onChange={(v) => updateExceptions({ zeroApr: v })}
            />
            <CheckboxRow
              label="DCA present but not acted upon"
              checked={Boolean(draft.exceptions?.dcaPresentNotActedUpon)}
              onChange={(v) => updateExceptions({ dcaPresentNotActedUpon: v })}
            />
            <CheckboxRow
              label="Captive or white-label tied"
              checked={Boolean(draft.exceptions?.captiveOrWhiteLabelTied)}
              onChange={(v) => updateExceptions({ captiveOrWhiteLabelTied: v })}
            />
          </div>

          {hasException ? (
            <div style={{ ...closureBoxStyle, marginTop: 16 }}>
              <div style={{ fontSize: 13, color: colors.neutral700, marginBottom: 8 }}>
                One or more exceptions apply — the case is ineligible.
              </div>
              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={applyDisabled(buttonDanger, closing || locked)}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (exception exists)'}
              </button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Gate 5 — Unfair relationship?                                       */}
      {/* ------------------------------------------------------------------ */}
      {showGate5 ? (
        <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 5 — Unfair relationship">
          <legend style={legendStyle}>Gate 5 — Unfair relationship</legend>

          <BoolRadio
            label="Unfair relationship present?"
            name="unfairRelationship"
            value={draft.unfairRelationship?.present}
            onChange={(v) => updateUnfairRelationship({ present: v })}
          />

          {draft.unfairRelationship?.present === false ? (
            <div style={closureBoxStyle}>
              <label style={labelStackStyle}>
                Reason relationship was fair
                <select
                  style={inputStyle}
                  value={draft.unfairRelationship?.fairnessReason ?? ''}
                  onChange={(e) =>
                    updateUnfairRelationship({
                      fairnessReason: (e.target.value as FairnessReason) || null,
                    })
                  }
                >
                  <option value="">— select reason —</option>
                  {FAIRNESS_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* FCA notification dates when tied-not-operated is chosen */}
              {draft.unfairRelationship?.fairnessReason ===
                'tied_arrangement_not_operated_in_practice' ? (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                  <label style={{ ...labelStackStyle, flex: 1, minWidth: 180 }}>
                    FCA notification due date
                    <span style={{ fontSize: 12, color: colors.neutral500, marginTop: -2 }}>
                      Scheme effective date + 6 weeks (derived)
                    </span>
                    <input
                      type="date"
                      readOnly
                      value={draft.unfairRelationship?.fcaNotificationDueDate ?? ''}
                      style={{ ...inputStyle, background: colors.neutral100 }}
                    />
                  </label>
                  <label style={{ ...labelStackStyle, flex: 1, minWidth: 180 }}>
                    FCA notification date
                    <input
                      type="date"
                      value={draft.unfairRelationship?.fcaNotificationDate ?? ''}
                      onChange={(e) =>
                        updateUnfairRelationship({
                          fcaNotificationDate: e.target.value || null,
                        })
                      }
                      style={inputStyle}
                    />
                  </label>
                </div>
              ) : null}

              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={{ ...applyDisabled(buttonDanger, closing || locked), marginTop: 12 }}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (no unfair relationship)'}
              </button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Gate 6 — Loss or damage?                                            */}
      {/* ------------------------------------------------------------------ */}
      {showGate6 ? (
        <fieldset style={fieldsetStyle} disabled={locked} aria-label="Gate 6 — Loss or damage">
          <legend style={legendStyle}>Gate 6 — Loss or damage</legend>

          <BoolRadio
            label="Loss or damage caused?"
            name="lossOrDamage"
            value={draft.lossOrDamage?.present}
            onChange={(v) => updateLossOrDamage({ present: v })}
          />

          {draft.lossOrDamage?.present === false ? (
            <div style={closureBoxStyle}>
              <div style={{ fontSize: 13, color: colors.neutral700, marginBottom: 8 }}>
                <strong>No loss reason:</strong> No better deal available
              </div>

              <label style={labelStackStyle}>
                Narrative
                <textarea
                  rows={3}
                  value={draft.lossOrDamage?.narrative ?? ''}
                  onChange={(e) => updateLossOrDamage({ narrative: e.target.value })}
                  style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                />
              </label>

              <div
                role="radiogroup"
                aria-label="Evidence option"
                style={{ marginTop: 8 }}
              >
                <div style={{ fontWeight: 500, fontSize: 13, color: colors.neutral800, marginBottom: 6 }}>
                  Evidence option
                </div>
                {(
                  [
                    {
                      value: 'option_a_alternative_aprs',
                      label: 'Option A — alternative APRs',
                    },
                    {
                      value: 'option_b_broker_internal_processes',
                      label: 'Option B — broker internal processes',
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      fontSize: 14,
                      color: colors.neutral800,
                      marginBottom: 4,
                    }}
                  >
                    <input
                      type="radio"
                      name="evidenceOption"
                      checked={draft.lossOrDamage?.evidenceOption === opt.value}
                      onChange={() => updateLossOrDamage({ evidenceOption: opt.value })}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>

              <button
                type="button"
                disabled={closing || locked}
                onClick={() => void handleClose()}
                style={{ ...applyDisabled(buttonDanger, closing || locked), marginTop: 12 }}
                aria-busy={closing}
              >
                {closing ? 'Closing…' : 'Save and close (no loss/damage)'}
              </button>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* Save without closing */}
      {!locked && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            style={applyDisabled(buttonPrimary, saving)}
            aria-busy={saving}
          >
            {saving ? 'Saving…' : 'Save liability'}
          </button>
        </div>
      )}

      {message !== null && (
        <div style={{ ...bannerSuccessStyle, marginTop: 12 }} role="status">
          {message}
        </div>
      )}
      {info !== null && (
        <div style={{ ...bannerInfoStyle, marginTop: 12 }} role="status">
          {info}
        </div>
      )}
      {error !== null && (
        <div style={{ ...bannerDangerStyle, marginTop: 12 }} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ClosedBannerProps {
  status: RedressStatus;
  onDownload: () => void;
  downloading: boolean;
}

function ClosedBanner({
  status,
  onDownload,
  downloading,
}: ClosedBannerProps): React.ReactElement {
  return (
    <div style={bannerWarnStyle} role="status">
      <div>
        <div style={{ fontWeight: 600, color: colors.warning, marginBottom: 2 }}>
          Agreement closed — inputs are locked.
        </div>
        <div style={{ color: colors.neutral700, fontSize: 13 }}>
          Status: <code>{status}</code> ({humaniseStatus(status)})
        </div>
      </div>
      <button
        type="button"
        onClick={onDownload}
        disabled={downloading}
        style={applyDisabled(buttonSecondary, downloading)}
        aria-busy={downloading}
      >
        {downloading ? 'Preparing…' : 'Download closure letter'}
      </button>
    </div>
  );
}

interface BoolRadioProps {
  label: string;
  name: string;
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
}

function BoolRadio({
  label,
  name,
  value,
  onChange,
}: BoolRadioProps): React.ReactElement {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontWeight: 500, color: colors.neutral800 }}>{label}</span>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name={name}
          checked={value === true}
          onChange={() => onChange(true)}
        />{' '}
        Yes
      </label>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        <input
          type="radio"
          name={name}
          checked={value === false}
          onChange={() => onChange(false)}
        />{' '}
        No
      </label>
    </div>
  );
}

interface ArrangementCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function ArrangementCheckbox({
  label,
  checked,
  onChange,
}: ArrangementCheckboxProps): React.ReactElement {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        border: `1px solid ${checked ? colors.primary : colors.neutral300}`,
        background: checked ? colors.primaryLight : colors.white,
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: checked ? 600 : 500,
        color: checked ? colors.primary : colors.neutral700,
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

interface CheckboxRowProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function CheckboxRow({
  label,
  checked,
  onChange,
}: CheckboxRowProps): React.ReactElement {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 14,
        color: colors.neutral800,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

// ---------------------------------------------------------------------------
// Inline styles
// ---------------------------------------------------------------------------

const arrangementRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 10,
};

const reasonsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 10,
};

const closureBoxStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 12,
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerLight,
  borderRadius: 6,
};
